import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { executiveAssignmentsApi, authApi } from '../api/services';
import { setActivePrincipalId as setClientPrincipalId } from '../api/client';
import type { ExecutiveAssignment } from '../types/models';

export interface Principal {
  id: string;
  name: string;
  email: string;
  isTenantAdmin: boolean;
}

interface PrincipalContextType {
  /** The principal the EA is currently acting on behalf of (null = own view) */
  activePrincipal: Principal | null;
  /** Set the active principal (pass null to clear) */
  setActivePrincipal: (principal: Principal | null) => void;
  /** Clear principal selection back to "My View" */
  clearPrincipal: () => void;
  /** List of assigned executives */
  principals: Principal[];
  /** Whether the EA is currently acting on behalf of a principal */
  isActingOnBehalf: boolean;
  /** Whether principal list is loading */
  loading: boolean;
}

const PrincipalContext = createContext<PrincipalContextType | null>(null);

export function usePrincipal() {
  const context = useContext(PrincipalContext);
  if (!context) throw new Error('usePrincipal must be used within PrincipalContextProvider');
  return context;
}

const SESSION_KEY = 'cadence_activePrincipal';

export function PrincipalContextProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isEa = user?.role === 'ExecutiveAssistant';

  const [principals, setPrincipals] = useState<Principal[]>([]);
  const [activePrincipal, setActivePrincipalState] = useState<Principal | null>(() => {
    if (!isEa) return null;
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Principal;
        // Immediately sync to API client on init
        setClientPrincipalId(parsed.id);
        return parsed;
      }
    } catch { /* ignore */ }
    return null;
  });
  const [loading, setLoading] = useState(false);

  // Track whether we've loaded principals for the current user to avoid re-fetching
  const loadedForUserRef = useRef<string | null>(null);

  // Load executive assignments when user is an EA
  useEffect(() => {
    if (!isEa || !user) {
      setPrincipals([]);
      return;
    }

    // Avoid re-fetching if we already loaded for this user
    if (loadedForUserRef.current === user.id) return;
    loadedForUserRef.current = user.id;

    const loadPrincipals = async () => {
      setLoading(true);
      try {
        const [assignments, users] = await Promise.all([
          executiveAssignmentsApi.getAll(),
          authApi.getUsers(),
        ]);

        const activeAssignments = assignments.filter((a: ExecutiveAssignment) => a.isActive);
        const principalList: Principal[] = activeAssignments.map((a: ExecutiveAssignment) => {
          const u = users.find((u: any) => u.id === a.executiveUserId);
          return {
            id: a.executiveUserId,
            name: u?.displayName || 'Unknown Executive',
            email: u?.email || '',
            isTenantAdmin: u?.isTenantAdmin || false,
          };
        });

        setPrincipals(principalList);

        // If we have a stored selection, validate it still exists in assignments
        if (activePrincipal) {
          const stillValid = principalList.some(p => p.id === activePrincipal.id);
          if (!stillValid) {
            setActivePrincipalState(null);
            setClientPrincipalId(null);
            sessionStorage.removeItem(SESSION_KEY);
          }
        }
      } catch {
        // Non-critical — EA workspace still works, just no global switching
        setPrincipals([]);
      } finally {
        setLoading(false);
      }
    };

    loadPrincipals();
  }, [isEa, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear principal context when user is not EA
  useEffect(() => {
    if (!isEa) {
      setActivePrincipalState(null);
      setClientPrincipalId(null);
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [isEa]);

  const setActivePrincipal = useCallback((principal: Principal | null) => {
    setActivePrincipalState(principal);
    if (principal) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(principal));
      setClientPrincipalId(principal.id);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
      setClientPrincipalId(null);
    }
  }, []);

  const clearPrincipal = useCallback(() => {
    setActivePrincipal(null);
  }, [setActivePrincipal]);

  const value: PrincipalContextType = {
    activePrincipal,
    setActivePrincipal,
    clearPrincipal,
    principals,
    isActingOnBehalf: activePrincipal !== null,
    loading,
  };

  return (
    <PrincipalContext.Provider value={value}>
      {children}
    </PrincipalContext.Provider>
  );
}
